import React from 'react';
import {
  Box, Container, Typography, Button, Paper, Stack, Avatar, Card, CardContent, Accordion, AccordionSummary, AccordionDetails, TextField, Alert, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import InsightsIcon from '@mui/icons-material/Insights';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StarIcon from '@mui/icons-material/Star';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import PolicyIcon from '@mui/icons-material/Policy';
import ReportIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import SEOForWebsite from '../SEOForWebsite';
import { submitForm } from '../../utils/formSubmission';

const brandBlue = '#0B60B1';

const keyFeatures = [
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Enterprise-grade Security',
    description: 'Advanced DMARC monitoring, reporting, and enforcement. Protect your domain and reputation.'
  },
  {
    icon: <InsightsIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Actionable Insights',
    description: 'Visualize authentication failures and get clear, actionable recommendations.'
  },
  {
    icon: <CheckCircleIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Automated Compliance',
    description: 'Automate DMARC compliance and policy enforcement with guided steps.'
  },
  {
    icon: <AccessTimeIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Real-time Monitoring',
    description: 'Monitor your domain in real-time. Receive instant alerts for suspicious activity.'
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 40, color: brandBlue }} />, title: 'Expert Support',
    description: 'Our deliverability experts are here to help you every step of the way.'
  }
];

const howItWorksSteps = [
  { step: 1, title: 'Connect Your Domain', desc: 'Easy onboarding with guided setup.' },
  { step: 2, title: 'Monitor & Analyze', desc: 'Get real-time DMARC, SPF, DKIM analytics.' },
  { step: 3, title: 'Take Action', desc: 'Receive recommendations and enforce policies.' },
  { step: 4, title: 'Stay Protected', desc: 'Ongoing monitoring and expert support.' }
];

const testimonials = [
  { name: 'Jane Doe', role: 'IT Manager', company: 'Acme Corp', quote: 'IQDMarc made DMARC compliance easy and gave us peace of mind. Highly recommended!', rating: 5 },
  { name: 'John Smith', role: 'CTO', company: 'TechFlow', quote: 'The actionable insights and real-time alerts are a game changer for our security team.', rating: 5 },
  { name: 'Priya Patel', role: 'Email Security Lead', company: 'SecureMail', quote: 'We improved our deliverability and stopped spoofing attacks with IQDMarc.', rating: 5 },
  { name: 'Carlos Ruiz', role: 'Head of IT', company: 'FinServe', quote: 'Setup was fast and the support team is fantastic.', rating: 5 },
  { name: 'Linda Zhang', role: 'Operations Director', company: 'MarketPro', quote: 'The dashboard is clear and the alerts are always timely.', rating: 4 },
  { name: 'Alex Kim', role: 'SysAdmin', company: 'CloudOps', quote: 'IQDMarc is a must-have for any business that cares about email security.', rating: 5 },
  { name: 'Sophie Dubois', role: 'Compliance Officer', company: 'RegulaTech', quote: 'We passed our audit thanks to IQDMarc\'s reporting.', rating: 5 },
  { name: 'Michael Brown', role: 'CEO', company: 'Startly', quote: 'Our team loves the simplicity and power of IQDMarc.', rating: 5 },
  { name: 'Fatima Al-Farsi', role: 'IT Security', company: 'MediTrust', quote: 'The best DMARC tool we\'ve used. Highly recommended.', rating: 5 },
  { name: 'Tom Müller', role: 'IT Consultant', company: 'Müller IT', quote: 'IQDMarc\'s analytics and support are top-notch.', rating: 5 },
];

const faqs = [
  { q: 'How long does setup take?', a: 'Most users are up and running in under 10 minutes.' },
  { q: 'Is my data secure?', a: 'Yes, we use enterprise-grade security and encryption.' },
  { q: 'Can I try before I buy?', a: 'Absolutely! Start with our Free or Pro trial.' },
  { q: 'What support is available?', a: 'All plans include email support. Pro and Enterprise get priority and dedicated support.' }
];

const IQDMarcProductPage: React.FC = () => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const cardsPerView = isSm ? 1 : isMd ? 2 : 3;
  const [carouselIndex, setCarouselIndex] = useState(0);
  const maxIndex = testimonials.length - cardsPerView;
  const handlePrev = () => setCarouselIndex(i => Math.max(0, i - 1));
  const handleNext = () => setCarouselIndex(i => Math.min(maxIndex, i + 1));

  // Form state
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitted(false);

    // Prepare form data with additional fields
    const formData = {
      name: form.name,
      email: form.email,
      company: form.company,
      message: form.message,
      page_name: 'iqdmarc',
      request_for: 'Request a Demo',
      submitted_at: new Date().toISOString()
    };

    // Submit using the utility
    await submitForm(formData, {
      formName: 'IQDMarc Demo Request',
      formSource: 'iqdmarc_product_page',
      onLoading: setSubmitting,
      onSuccess: (response) => {
        setSubmitted(true);
        setForm({ name: '', email: '', company: '', message: '' });
        console.log('Demo request submitted successfully:', response);
      },
      onError: (error) => {
        setError(error.message || 'Failed to submit demo request. Please try again.');
        console.error('Demo request submission error:', error);
      }
    });
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh' }}>
      <SEOForWebsite 
        title="IQDMarc - DMARC Monitoring & Management Platform"
        description="Secure your domain and protect your brand with IQDMarc. Advanced DMARC monitoring, reporting, and enforcement platform for email authentication."
        keywords="DMARC, email authentication, domain security, email spoofing, phishing prevention, SPF, DKIM"
        canonical="/products/iqdmarc"
      />
      {/* 1. Hero Section */}
      <Box sx={{ bgcolor: brandBlue, color: '#fff', py: { xs: 6, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h3" fontWeight={700} gutterBottom>IQDMarc</Typography>
          <Typography variant="h5" fontWeight={400} gutterBottom>
            Secure your domain. Protect your brand. Master DMARC.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 3 }}>
            <Button
              variant="contained" color="secondary" size="large" sx={{ fontWeight: 600 }}
              href="/tools/dmarc-analyzer"
              component="a"
            >
              Audit Domain
            </Button>
            <Button variant="outlined" color="inherit" size="large" sx={{ fontWeight: 600, borderColor: '#fff', color: '#fff' }}
              href="#request-demo"
              component="a"
            >
              Request a Demo
            </Button>
          </Stack>
          <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
            <DashboardIcon sx={{ fontSize: 80, opacity: 0.12 }} />
          </Box>
        </Container>
      </Box>

      {/* 2. Product Overview */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight={800} color="primary" gutterBottom>
            What is IQDMarc?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, textAlign: 'justify' }}>
            IQDMarc is the modern SaaS platform for proactive email authentication management. We empower IT teams, MSPs, and security leaders to prevent phishing, stop domain spoofing, and ensure every message you send is trusted.
          </Typography>
        </Box>
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: '#e3f0fc', display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <InfoOutlinedIcon color="primary" sx={{ fontSize: 36, mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
              Did you know?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <b>Email domain spoofing</b> is when attackers forge your domain to send fraudulent emails. This can lead to phishing, data breaches, and loss of customer trust. DMARC, SPF, and DKIM are essential protocols that protect your brand and your customers from these threats.
            </Typography>
          </Box>
        </Paper>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'justify' }}>
          With IQDMarc, you get real-time monitoring, actionable insights, and expert guidance to keep your domain secure and your communications trusted—no matter your organization's size or complexity.
        </Typography>
      </Container>

      {/* How DMARC Works Section */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>How DMARC Works</Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
          DMARC (Domain-based Message Authentication, Reporting & Conformance) gives domain owners control and visibility over who can send email on their behalf. Here's how the process works:
        </Typography>
        <Box sx={{ bgcolor: '#f7fafd', borderRadius: 3, p: { xs: 2, md: 4 }, mb: 3, boxShadow: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems="center" justifyContent="center">
            {/* Left: Email Received & DMARC Check */}
            <Stack spacing={2} alignItems="center" sx={{ minWidth: 180 }}>
              <EmailIcon sx={{ fontSize: 40, color: brandBlue }} />
              <Typography fontWeight={600} textAlign="center">Email Received<br />by Mailbox Provider</Typography>
              <ArrowDownwardIcon sx={{ color: 'grey.500' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">Has DMARC been implemented for "Header From" domain?</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
                <CancelIcon sx={{ color: 'error.main' }} />
              </Stack>
              <ArrowDownwardIcon sx={{ color: 'grey.500' }} />
              <Typography fontWeight={600} textAlign="center">Does email pass DMARC authentication?</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 32 }} />
                <CancelIcon sx={{ color: 'error.main', fontSize: 32 }} />
              </Stack>
            </Stack>
            {/* Center: Arrows and Decision */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', height: 260, justifyContent: 'center' }}>
              <ArrowForwardIcon sx={{ color: 'success.main', fontSize: 40, mb: 2 }} />
              <ArrowForwardIcon sx={{ color: 'error.main', fontSize: 40, mt: 2 }} />
            </Box>
            {/* Right: DMARC Control & Visibility */}
            <Stack spacing={2} alignItems="center" sx={{ minWidth: 220, bgcolor: '#e3f0fc', borderRadius: 2, p: 2 }}>
              <Typography fontWeight={700} color="primary" textAlign="center">DMARC Control & Visibility</Typography>
              <PolicyIcon sx={{ color: brandBlue, fontSize: 36 }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">If DMARC fails, apply domain owner's policy:</Typography>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                <Box display="flex" flexDirection="column" alignItems="center">
                  <DeleteOutlineIcon sx={{ color: 'warning.main', fontSize: 32 }} />
                  <Typography variant="caption">Quarantine</Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <CancelIcon sx={{ color: 'error.main', fontSize: 32 }} />
                  <Typography variant="caption">Reject</Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <FilterAltIcon sx={{ color: 'grey.600', fontSize: 32 }} />
                  <Typography variant="caption">None</Typography>
                </Box>
              </Stack>
              <ArrowDownwardIcon sx={{ color: 'grey.500' }} />
              <ReportIcon sx={{ color: brandBlue, fontSize: 32 }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">Deliver report to sender for visibility & action</Typography>
            </Stack>
          </Stack>
        </Box>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          DMARC helps you protect your domain from unauthorized use, improve deliverability, and gain insight into who is sending email on your behalf.
        </Typography>
      </Container>

      {/* 3. Key Features */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>Key Features</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch" justifyContent="center">
          {keyFeatures.slice(0, 3).map((feature, idx) => (
            <Paper key={idx} elevation={3} sx={{ flex: 1, p: 4, textAlign: 'center', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 10 } }}>
              {feature.icon}
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>{feature.title}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{feature.description}</Typography>
            </Paper>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch" justifyContent="center" sx={{ mt: 3 }}>
          {keyFeatures.slice(3).map((feature, idx) => (
            <Paper key={idx} elevation={3} sx={{ flex: 1, p: 4, textAlign: 'center', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 10 } }}>
              {feature.icon}
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>{feature.title}</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{feature.description}</Typography>
            </Paper>
          ))}
        </Stack>
      </Container>

      {/* 4. How It Works */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>How It Works</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch" justifyContent="center">
          {howItWorksSteps.map((step, idx) => (
            <Paper key={idx} elevation={2} sx={{ flex: 1, p: 3, textAlign: 'center', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <Avatar sx={{ bgcolor: brandBlue, width: 48, height: 48, fontWeight: 700, mb: 1 }}>{step.step}</Avatar>
              <Typography fontWeight={600} mt={2}>{step.title}</Typography>
              <Typography variant="body2" color="text.secondary">{step.desc}</Typography>
            </Paper>
          ))}
        </Stack>
      </Container>

      {/* 5. Customer Testimonials / Social Proof */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>What Our Customers Say</Typography>
        <Box sx={{ position: 'relative', width: '100%', mt: 3, px: { xs: 0, sm: 2 } }}>
          <Button
            onClick={handlePrev}
            disabled={carouselIndex === 0}
            sx={{ position: 'absolute', left: { xs: 0, sm: -32 }, top: '50%', transform: 'translateY(-50%)', zIndex: 1, minWidth: 0, p: 1, bgcolor: '#fff', boxShadow: 2, borderRadius: '50%' }}
            aria-label="Previous"
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </Button>
          <Stack direction="row" spacing={3} justifyContent="center" alignItems="stretch" sx={{ overflow: 'hidden', width: '100%' }}>
            {testimonials.slice(carouselIndex, carouselIndex + cardsPerView).map((t, idx) => (
              <Card key={carouselIndex + idx} elevation={2} sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
                <CardContent sx={{ flexGrow: 1, width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar>{t.name[0]}</Avatar>
                    <Box>
                      <Typography fontWeight={600}>{t.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.role}, {t.company}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 1, textAlign: 'justify' }}>
                    "{t.quote}"
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {[...Array(t.rating)].map((_, i) => (
                      <StarIcon key={i} sx={{ color: '#FFD700', fontSize: 20 }} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Button
            onClick={handleNext}
            disabled={carouselIndex >= maxIndex}
            sx={{ position: 'absolute', right: { xs: 0, sm: -32 }, top: '50%', transform: 'translateY(-50%)', zIndex: 1, minWidth: 0, p: 1, bgcolor: '#fff', boxShadow: 2, borderRadius: '50%' }}
            aria-label="Next"
          >
            <ArrowForwardIosIcon fontSize="small" />
          </Button>
        </Box>
      </Container>

      {/* 6. Email Security Points + Form CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={5} alignItems="stretch" justifyContent="center">
          {/* Left: Email Security Points */}
          <Box flex={1} display="flex" flexDirection="column" justifyContent="center" sx={{ mb: { xs: 4, md: 0 } }}>
            <Typography variant="h4" fontWeight={700} gutterBottom color="primary">Why Email Security Matters</Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2, color: 'text.secondary', fontSize: 18 }}>
              <li>Protect your brand from phishing and spoofing attacks</li>
              <li>Ensure only authorized senders use your domain</li>
              <li>Boost deliverability and customer trust</li>
              <li>Meet compliance and audit requirements</li>
              <li>Get real-time alerts and actionable insights</li>
              <li>Expert support for your IT and compliance teams</li>
            </Box>
            <Typography variant="body2" color="text.secondary">
              IQDMarc is trusted by organizations worldwide to secure their email infrastructure and reputation.
            </Typography>
          </Box>
          {/* Right: Form */}
          <Box flex={1}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }} id="request-demo">
              <Typography variant="h5" fontWeight={600} gutterBottom>Request a Demo</Typography>
              <Box component="form" onSubmit={handleFormSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField 
                  label="Name" 
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  variant="outlined" 
                  required 
                  fullWidth 
                />
                <TextField 
                  label="Email" 
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  variant="outlined" 
                  required 
                  fullWidth 
                  type="email" 
                />
                <TextField 
                  label="Company" 
                  name="company"
                  value={form.company}
                  onChange={handleFormChange}
                  variant="outlined" 
                  required 
                  fullWidth 
                />
                <TextField 
                  label="Message or Request" 
                  name="message"
                  value={form.message}
                  onChange={handleFormChange}
                  variant="outlined" 
                  multiline 
                  minRows={3} 
                  fullWidth 
                />
                {error && <Alert severity="error">{error}</Alert>}
                {submitted && <Alert severity="success">Thank you! We've received your demo request and will be in touch soon.</Alert>}
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large" 
                  type="submit"
                  disabled={submitting}
                  sx={{ fontWeight: 600, mt: 1 }}
                  endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {submitting ? 'Sending...' : 'Request Demo'}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Stack>
      </Container>

      {/* 7. FAQ + Final CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Typography variant="h4" fontWeight={700} textAlign="center" gutterBottom>Frequently Asked Questions</Typography>
        <Box sx={{ mb: 4 }}>
          {faqs.map((faq, idx) => (
            <Accordion key={idx} TransitionProps={{ timeout: 500 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large" 
            sx={{ fontWeight: 700, px: 6, py: 2, fontSize: 20 }}
            href="#request-demo"
            component="a"
          >
            Start Free Trial
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default IQDMarcProductPage; 